import type { FC } from 'react';

import CsPlayerList from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/CsPlayerList';
import CsMapBanner from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/CsMapBanner';
import CsMapRow from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/CsMapRow';
import InterGameBreak from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/InterGameBreak';
import {
  getCsPlayers,
  hasField
} from 'app/components/DesignVersion/Grad/Blocks/NameBlockGrad/utils';

import style from './style.module.scss';

interface Props {
  availableFields: CsAvailableField[];
  // The game on display — feeds the banner and the player lists.
  game: AggregatedGameCs;
  // The whole series — the map row walks all of it.
  games: AggregatedGameCs[];
  team1: AggregatedTeamCsAny | undefined;
  team2: AggregatedTeamCsAny | undefined;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  // Inter-game break: when set, the "takes the victory! / Break" card floats
  // over the banner + maps areas (the rosters stay visible on either side).
  breakWinner?: OpponentBase;
  breakWinnerIndex?: 0 | 1 | null;
}

// cs-go live section, rendered below the score card — the counterpart of
// Dota2DetailedSection. Every sub-view is gated on the field that feeds it
// rather than on the game's `detailed` flag: the rosters need player_name, the
// banner score needs round_score, the map art needs map_id.
//
// Laid out as a 4-area grid (banner / maps / list-team1 / list-team2) that
// reflows per breakpoint — see style.module.scss. Without rosters, banner+maps
// fall back to a single stacked column instead of reserving empty roster
// columns.
const CsDetailedSection: FC<Props> = ({
  availableFields,
  game,
  games,
  team1,
  team2,
  opponent1,
  opponent2,
  breakWinner,
  breakWinnerIndex
}) => {
  const players1 = getCsPlayers(team1);
  const players2 = getCsPlayers(team2);
  const showPlayers =
    hasField(availableFields, 'player_name') && Boolean(players1.length || players2.length);

  const showScore = hasField(availableFields, 'round_score');
  const hasMapId = hasField(availableFields, 'map_id');
  // Team objects always carry a `side`, even when the frame doesn't advertise
  // it — so the roster must gate on the field itself, not on the value.
  const hasSide = hasField(availableFields, 'side');
  const showBanner = hasMapId || showScore;
  // No map_id: the map art is unknowable, so the banner falls back to the
  // no-image gradient even though `game.map` itself is always present.
  const bannerMap = hasMapId ? game.map : undefined;
  // Self-gating per card, so it only needs one game in the series to have a map.
  const showMapRow = games.some(seriesGame => Boolean(seriesGame.map));

  const breakOverlay =
    breakWinner && (breakWinnerIndex === 0 || breakWinnerIndex === 1) ? (
      <InterGameBreak winnerName={breakWinner.name} winnerIndex={breakWinnerIndex} />
    ) : null;

  // No sub-view has data. The break card is still worth showing, but it is
  // absolutely positioned, so it needs a box of its own to cover.
  if (!showPlayers && !showBanner && !showMapRow) {
    if (!breakOverlay) return null;
    return <div className={style.breakOnly}>{breakOverlay}</div>;
  }

  const banner = showBanner && (
    <div className={style.banner}>
      <CsMapBanner map={bannerMap} team1={team1} team2={team2} showScore={showScore} />
    </div>
  );

  const maps = showMapRow && (
    <div className={style.maps}>
      <CsMapRow games={games} opponent1={opponent1} opponent2={opponent2} currentGameId={game.id} />
    </div>
  );

  if (!showPlayers) {
    return (
      <div className={style.gridBasic}>
        {banner}
        {maps}
        {breakOverlay}
      </div>
    );
  }

  return (
    <div className={style.grid}>
      <div className={style.listTeam1}>
        <CsPlayerList
          players={players1}
          side={hasSide ? team1?.side ?? null : null}
          teamIndex={0}
          availableFields={availableFields}
        />
      </div>
      {banner}
      {maps}
      <div className={style.listTeam2}>
        <CsPlayerList
          players={players2}
          side={hasSide ? team2?.side ?? null : null}
          teamIndex={1}
          availableFields={availableFields}
        />
      </div>
      {breakOverlay}
    </div>
  );
};

CsDetailedSection.displayName = 'CsDetailedSection';

export default CsDetailedSection;
